import { Divider, Popover } from "antd";
import type { ReactNode } from "react";
import styled from "styled-components";
import { getPropRuntimeGraphDescription } from "../../../shared/types/PropRuntimeGraphUtils";
import type { PropRuntimeGraph } from "../../game/assets/shared/types/PropRuntimeGraph";
import type { RuntimePropInstance } from "../../game/assets/pri_wqs/scripts/core/propMechanics/PropMechanicsTypes";
import {
  formatSignedNumber,
  getRuntimeGraphSummary,
  getRuntimeInstanceName,
  getSandboxPropTypeLabel,
  getSandboxRarityLabel,
  getSandboxTagLabel,
  isRuntimeEmptySlot,
  readRuntimeYield,
} from "./runtimeBridge";

export interface PropPreviewProps {
  item?: RuntimePropInstance;
  graph?: PropRuntimeGraph;
  catalog?: PropRuntimeGraph[];
  children: ReactNode;
}

export function PropPreview({ item, graph, catalog = [], children }: PropPreviewProps) {
  const targetGraph = item?.graph ?? graph;
  if (!targetGraph) return <>{children}</>;

  return (
    <Popover
      mouseEnterDelay={0.2}
      placement="right"
      overlayStyle={{ maxWidth: 380 }}
      styles={{
        body: {
          background: "#101827",
          border: "1px solid #26364f",
          borderRadius: 8,
          boxShadow: "0 16px 42px rgba(0, 0, 0, .45)",
        },
      }}
      content={<PropPreviewContent item={item} graph={targetGraph} catalog={catalog} />}
    >
      <PreviewAnchor>{children}</PreviewAnchor>
    </Popover>
  );
}

function PropPreviewContent({ item, graph, catalog }: { item?: RuntimePropInstance; graph: PropRuntimeGraph; catalog: PropRuntimeGraph[] }) {
  const empty = item ? isRuntimeEmptySlot(item) : graph.name === "空位置";
  const description = empty ? "" : getRuntimeDescription(graph, catalog);
  const tags = (graph.tags ?? []).map(getSandboxTagLabel).join("、") || "无";
  const coordinate = item && item.row >= 0 && item.col >= 0 ? `${item.row}, ${item.col}` : "未在盘面";

  return (
    <PreviewPanel>
      <PreviewTitle>{graph.name || graph._id}</PreviewTitle>
      <PreviewSubTitle>{getRuntimeGraphSummary(graph)}</PreviewSubTitle>
      {empty ? (
        <PreviewGrid>
          <PreviewLabel>实例ID</PreviewLabel><PreviewValue>{item?.id ?? "未实例化"}</PreviewValue>
          <PreviewLabel>原型ID</PreviewLabel><PreviewValue>{graph._id}</PreviewValue>
          <PreviewLabel>坐标</PreviewLabel><PreviewValue>{coordinate}</PreviewValue>
          <PreviewLabel>状态</PreviewLabel><PreviewValue>{item?.alive === false ? "失效" : "有效"}</PreviewValue>
        </PreviewGrid>
      ) : (
        <>
          <PreviewGrid>
            <PreviewLabel>名称</PreviewLabel><PreviewValue>{getRuntimeInstanceName(item) || graph.name || "-"}</PreviewValue>
            <PreviewLabel>资源ID</PreviewLabel><PreviewValue>{graph.assetId || "-"}</PreviewValue>
            <PreviewLabel>原型ID</PreviewLabel><PreviewValue>{graph._id}</PreviewValue>
            <PreviewLabel>实例ID</PreviewLabel><PreviewValue>{item?.id ?? "未实例化"}</PreviewValue>
            <PreviewLabel>类型</PreviewLabel><PreviewValue>{getSandboxPropTypeLabel(graph.type)}</PreviewValue>
            <PreviewLabel>品质</PreviewLabel><PreviewValue>{getSandboxRarityLabel(graph.rarity)}</PreviewValue>
            <PreviewLabel>标签</PreviewLabel><PreviewValue>{tags}</PreviewValue>
            <PreviewLabel>衍生物</PreviewLabel><PreviewValue>{graph.derived ? "是" : "否"}</PreviewValue>
            <PreviewLabel>基础产出</PreviewLabel><PreviewValue>{graph.yield ?? 0}</PreviewValue>
            <PreviewLabel>永久成长</PreviewLabel><PreviewValue>{formatSignedNumber(item?.grow ?? 0)}</PreviewValue>
            <PreviewLabel>当前产出</PreviewLabel><PreviewValue>{item ? readRuntimeYield(item) : graph.yield ?? 0}</PreviewValue>
            <PreviewLabel>坐标</PreviewLabel><PreviewValue>{coordinate}</PreviewValue>
            <PreviewLabel>存活状态</PreviewLabel><PreviewValue>{item?.alive === false ? "失效" : "有效"}</PreviewValue>
            <PreviewLabel>使用次数</PreviewLabel><PreviewValue>{item?.useCount ?? 0}</PreviewValue>
            <PreviewLabel>触发次数</PreviewLabel><PreviewValue>{item?.triggerCount ?? 0}</PreviewValue>
            <PreviewLabel>保护层数</PreviewLabel><PreviewValue>{item?.protectStacks ?? 0}</PreviewValue>
            <PreviewLabel>已收集魔力</PreviewLabel><PreviewValue>{item?.collectedMagic ?? 0}</PreviewValue>
            <PreviewLabel>已净化</PreviewLabel><PreviewValue>{item?.purified ? "是" : "否"}</PreviewValue>
            <PreviewLabel>负面效果</PreviewLabel><PreviewValue>{formatList(item?.negativeEffects)}</PreviewValue>
          </PreviewGrid>
          {item?.meta && Object.keys(item.meta).length > 0 && (
            <>
              <PreviewDivider />
              <PreviewBlockTitle>运行时附加数据</PreviewBlockTitle>
              <PreviewCode>{JSON.stringify(item.meta, null, 2)}</PreviewCode>
            </>
          )}
          <PreviewDivider />
          <PreviewBlockTitle>描述文本</PreviewBlockTitle>
          <PreviewDescription>{description || "无"}</PreviewDescription>
        </>
      )}
    </PreviewPanel>
  );
}

function getRuntimeDescription(graph: PropRuntimeGraph, catalog: PropRuntimeGraph[]) {
  try {
    return getPropRuntimeGraphDescription(graph as any, {
      types: Object.keys({ 1: true, 2: true, 3: true }).map((value) => ({ value: Number(value), label: getSandboxPropTypeLabel(Number(value)) })),
      rarities: Object.keys({ 1: true, 2: true, 3: true, 4: true }).map((value) => ({ value: Number(value), label: getSandboxRarityLabel(Number(value)) })),
      tags: [1, 2, 3, 4, 5, 6, 7, 8, 99].map((value) => ({ value, label: getSandboxTagLabel(value) })),
      props: catalog.map((item) => ({ _id: item._id, name: item.name, assetId: item.assetId })),
    });
  } catch (error) {
    return `描述生成失败：${error instanceof Error ? error.message : String(error)}`;
  }
}

function formatList(value: unknown) {
  if (!Array.isArray(value) || !value.length) return "无";
  return value.map((item) => typeof item === "string" ? item : JSON.stringify(item)).join("、");
}

const PreviewAnchor = styled.span`
  display: block;
  width: 100%;
  height: 100%;
`;

const PreviewPanel = styled.div`
  width: 360px;
  max-width: calc(100vw - 32px);
  color: #d8e2ef;
  font-size: 12px;
  line-height: 1.5;
`;

const PreviewTitle = styled.div`
  color: #f8fafc;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.4;
  word-break: break-word;
`;

const PreviewSubTitle = styled.div`
  color: #94a3b8;
  margin-top: 2px;
  word-break: break-word;
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: 76px minmax(0, 1fr);
  gap: 5px 8px;
  margin-top: 10px;
`;

const PreviewLabel = styled.div`
  color: #8ea0b8;
  white-space: nowrap;
`;

const PreviewValue = styled.div`
  color: #e2e8f0;
  min-width: 0;
  word-break: break-all;
`;

const PreviewDivider = styled(Divider)`
  margin: 10px 0 8px !important;
  border-color: #26364f !important;
`;

const PreviewBlockTitle = styled.div`
  color: #f1f5f9;
  font-weight: 600;
  margin-bottom: 4px;
`;

const PreviewDescription = styled.div`
  color: #d8e2ef;
  white-space: pre-wrap;
  word-break: break-word;
`;

const PreviewCode = styled.pre`
  max-height: 96px;
  overflow: hidden;
  margin: 0;
  padding: 6px;
  color: #cbd5e1;
  background: #0b1220;
  border: 1px solid #26364f;
  border-radius: 6px;
  white-space: pre-wrap;
  word-break: break-word;
`;
